import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLang } from '../../context/LangContext';
import { useAudio } from '../../context/AudioContext';
import { fr } from '../../i18n/fr';
import { en } from '../../i18n/en';
import { de } from '../../i18n/de';

const T = { fr, en, de };
const EASE = [0.16, 1, 0.3, 1];

const TRACKS = [
  { n: '01', t: 'None of this Shit will last', d: '4:48' },
  { n: '02', t: 'Reason',                      d: '5:02' },
  { n: '03', t: 'Voidy Inside',                d: '4:36' },
  { n: '04', t: 'Pink Truck',                  d: '3:55' },
  { n: '05', t: 'Lost my Way',                 d: '4:21' },
  { n: '06', t: 'Darker Days',                 d: '5:14' },
  { n: '07', t: 'Voidy Inside (Remix)',         d: '6:08' },
];

const STREAMS = [
  { name: 'SoundCloud', url: 'https://soundcloud.com/tsanga-berlin' },
  { name: 'Bandcamp',   url: 'https://tsanga.bandcamp.com/album/a-piece-of-sky' },
  { name: 'Spotify',    url: 'https://open.spotify.com/intl-fr/artist/6SAdpFlN4N8Wg4UNcCIEyz' },
  { name: 'Apple Music',url: 'https://music.apple.com/fr/artist/tsanga/1720855856' },
];

export default function Record() {
  const [flipped, setFlipped] = useState(false);
  const { lang } = useLang();
  const t = T[lang]?.record || T.fr.record;
  const { current, playing, setCurrent, setPlaying } = useAudio();

  return (
    <section style={{ position: 'relative', minHeight: '100vh', background: 'var(--bg)', padding: '80px 56px 100px' }}>
      <div className="noise" aria-hidden="true" />

      {/* Chapter bar */}
      <motion.div
        initial={{ opacity: 0, y: -10 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }} transition={{ duration: 0.7, ease: EASE }}
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          paddingBottom: 18, borderBottom: '1px solid var(--line)', marginBottom: 56,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
          <span style={{ fontFamily: 'var(--display)', fontSize: 32, fontWeight: 300 }}>III.</span>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '0.22em', textTransform: 'uppercase' }}>{(T[lang]?.nav || T.fr.nav).record}</span>
        </div>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.18em', color: 'var(--faint)', textTransform: 'uppercase' }}>
          CSC-007 · LP · 04·25 · 38m 14s
        </span>
      </motion.div>

      {/* 2-col layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '72px', alignItems: 'start' }}>

        {/* Left column */}
        <motion.div
          initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE }}
          style={{ display: 'flex', flexDirection: 'column', gap: 24 }}
        >
          <p style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.2em', color: 'var(--faint)', textTransform: 'uppercase' }}>{t.kicker}</p>

          <h2 style={{ fontFamily: 'var(--display)', fontWeight: 300, fontSize: 'clamp(64px,9vw,130px)', lineHeight: 0.86, letterSpacing: '-0.01em', margin: 0 }}>
            <span style={{ display: 'block' }}>A Piece</span>
            <span style={{ display: 'block', fontFamily: 'var(--serif)', fontStyle: 'italic', fontWeight: 300, fontSize: '0.55em', color: 'rgba(255,255,255,0.7)', paddingLeft: '0.6em' }}>/of/</span>
            <span style={{ display: 'block' }}>Sky</span>
          </h2>

          <p style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 17, lineHeight: 1.6, color: 'rgba(255,255,255,0.75)', maxWidth: 480 }}>
            {t.quote}
            <span style={{ display: 'block', marginTop: 12, fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.18em', color: 'var(--faint)', fontStyle: 'normal' }}>{t.credit}</span>
          </p>

          {/* Streaming links */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, maxWidth: 460 }}>
            {STREAMS.map(({ name, url }) => (
              <motion.a
                key={name}
                href={url}
                {...(url !== '#' ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                whileHover={{ borderColor: 'rgba(255,255,255,0.7)', color: '#fff' }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 18px', border: '1px solid var(--line)',
                  color: 'var(--muted)', fontFamily: 'var(--mono)', fontSize: 11,
                  letterSpacing: '0.14em', textDecoration: 'none',
                  transition: 'border-color 0.2s, color 0.2s',
                }}
              >
                {name} <span style={{ opacity: 0.4, fontSize: 14 }}>↗</span>
              </motion.a>
            ))}
          </div>
        </motion.div>

        {/* Right column: vinyl + tracklist */}
        <motion.div
          initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
          style={{ display: 'flex', flexDirection: 'column', gap: 28 }}
        >
          {/* Vinyl flip container */}
          <div
            style={{ position: 'relative', height: 320, perspective: '1600px', cursor: 'none' }}
            onClick={() => setFlipped(f => !f)}
            role="button"
            aria-label={t.hint}
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && setFlipped(f => !f)}
          >
            <motion.div
              style={{ position: 'relative', width: 320, height: 320, transformStyle: 'preserve-3d' }}
              animate={{ rotateY: flipped ? 180 : 0 }}
              transition={{ duration: 1.1, ease: [0.4, 0, 0.2, 1] }}
            >
              {/* Vinyl disc (only visible when not flipped) */}
              <motion.div
                animate={{ opacity: flipped ? 0 : 1 }}
                transition={{ duration: 0.3, delay: flipped ? 0 : 0.6 }}
                style={{
                position: 'absolute', inset: 0, borderRadius: '50%',
                background: 'radial-gradient(circle, #1a1a1a 0%, #080808 40%, #040404 100%)',
                display: 'grid', placeItems: 'center',
                boxShadow: '0 8px 40px rgba(0,0,0,0.8)',
              }}>
                <div style={{ width: 96, height: 96, borderRadius: '50%', background: '#fff', display: 'grid', placeItems: 'center' }}>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 500, color: '#000', letterSpacing: '0.1em', textAlign: 'center', lineHeight: 1.4 }}>CSC<br/>RECORDS</span>
                </div>
              </motion.div>

              {/* Face front */}
              <div style={{ position: 'absolute', inset: 0, backfaceVisibility: 'hidden', overflow: 'hidden' }}>
                <img
                  src="/pochette front.JPG"
                  alt="A Piece of Sky — recto"
                  loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              </div>

              {/* Face back */}
              <div style={{ position: 'absolute', inset: 0, backfaceVisibility: 'hidden', transform: 'rotateY(180deg)', overflow: 'hidden' }}>
                <img
                  src="/pochette back.JPG"
                  alt="A Piece of Sky — verso"
                  loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              </div>
            </motion.div>

          </div>

          {/* Hint text */}
          <p style={{ marginTop: 12, fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.2em', color: 'var(--faint)', textTransform: 'uppercase' }}>
            {t.hint}
          </p>

          {/* Tracklist */}
          <div style={{ borderTop: '1px solid var(--line)', marginTop: 36 }}>
            {TRACKS.map((track, i) => {
              const isCurrent = current === i;
              const isPlaying = isCurrent && playing;
              const onToggle = () => {
                if (isCurrent) setPlaying(p => !p);
                else { setCurrent(i); setPlaying(true); }
              };
              return (
                <motion.button
                  key={track.n}
                  onClick={onToggle}
                  whileHover={{ background: 'rgba(255,255,255,0.04)' }}
                  aria-label={`${isPlaying ? 'Pause' : 'Play'} ${track.t}`}
                  style={{
                    width: '100%', textAlign: 'left',
                    background: isCurrent ? 'rgba(255,255,255,0.03)' : 'transparent',
                    border: 0, borderBottom: '1px solid var(--line)',
                    display: 'grid',
                    gridTemplateColumns: '36px 1fr 60px 24px',
                    gap: 16, padding: '11px 8px',
                    alignItems: 'center', cursor: 'pointer',
                    color: 'inherit',
                    transition: 'background 0.15s',
                  }}
                >
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--faint)', letterSpacing: '0.1em' }}>{track.n}</span>
                  <span style={{ fontFamily: 'var(--display)', fontSize: 16, fontWeight: 300, letterSpacing: '0.02em', color: isCurrent ? '#fff' : 'rgba(255,255,255,0.85)' }}>{track.t}</span>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--faint)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{track.d}</span>
                  <span style={{ fontSize: 10, color: isCurrent ? '#fff' : 'rgba(255,255,255,0.3)', textAlign: 'right' }} aria-hidden="true">{isPlaying ? '❚❚' : '▶'}</span>
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
